import {
  ConnectableElement,
  Connector,
  ConnectorInstance,
  ConnectorParameters,
  Datapoint,
  DefaultDatapointTypes,
  EventDatapointParameters,
  JSAPIDatapointParameters,
  JSAPIInstanceParameters,
  JSONObject,
  JSONValue,
  TYPE
} from '@olive/olive-types';
import { replacePlaceholdersLocal, replacePlaceholdersObjectLocal } from '@olive/placeholder-util-browser';
import { isConnectorDefinition, isConnectorInstance, isConnectorTask, isDatapointDefinition, isDatapointInstance } from '@olive/types-util';
import * as EventEmitter from 'events';
import { getDatapointFromConnectorElement } from '../../utils/ConnectivityFinder';
import APIManager from '../APIManager';
import ConnectivityHolder, { ConnectivityDatapoint } from '../ConnectivityHolder';
import ConnectivityManager from '../ConnectivityManager';
import ENV_VARS from '../EnvironmentVariables';
import AbstractDispatcher from './AbstractDispatcher';
import WindowDispatcher from './WindowDispatcher';

type DispatchDatapointProps = {
  id: string;
  input?: object;
  parameters?: {
    componentInstanceID: string;
  };
};

const SUPPORTED_TYPES = [
  'olive',
  'event',
  'jsAPI',
  'windowmessage',
  'broadcast',
];

export const CLIENT_DISPATCHER_ID = 'client-dispatcher';

export default class ClientDispatcher extends AbstractDispatcher {
  eventManager: EventEmitter;
  apiManager: APIManager;
  connectivityManager: ConnectivityManager;
  // broadcastDispatcher: BroadcastDispatcher;
  windowDispatcher: WindowDispatcher;

  constructor(
    connectivityHolder: ConnectivityHolder,
    eventManager: EventEmitter,
    apiManager: APIManager,
    connectivityManager: ConnectivityManager
  ) {
    super(connectivityHolder);
    this.eventManager = eventManager;
    this.apiManager = apiManager;
    this.connectivityManager = connectivityManager;
    this.windowDispatcher = new WindowDispatcher(
      connectivityHolder,
      connectivityManager
    );
  }

  async dispatch({ id, task, input }: { id: string, task?: ConnectableElement, input?: object }): Promise<unknown> {
    const datapoint = task || this.connectivityHolder.get(id) as ConnectableElement;

    console.log(datapoint, datapoint.type);
    switch (datapoint.type) {
      case TYPE.CONNECTOR:  
      case 'connectorInstance':
        return await this.dispatchConnector(
          datapoint as Connector,
          input
        );
      case TYPE.DATAPOINT:
        return await this.dispatchDatapoint(
          datapoint as ConnectivityDatapoint,
          input
        );
      case TYPE.WORKFLOW:
        return await this.connectivityManager.invoke({
          id: datapoint.id,
          input,
        });
      default:
        console.warn(`Datapoint Type ${datapoint.type} not supported!`);
        return `Datapoint Type ${datapoint.type} not supported`;
    }
  }

  canDispatch(props: { id: string }): boolean {
    try {
      const { datapoint, type } = this.connectivityHolder.get(props.id) as Datapoint;
      if ([TYPE.CONNECTOR, 'connectorInstance'].includes(type)) {
        return true;
      }
      const datapointType = datapoint.type;
      return SUPPORTED_TYPES.includes(datapointType);
    } catch {
      return false;
    }
  }

  canDispatchType(type: string): boolean {
    return SUPPORTED_TYPES.includes(type);
  }

  /**
   * Gets the connector configuration from a connector
   */
  private getConnectorConfig(connector: Connector | ConnectorInstance): ConnectorParameters | null {
    if (isConnectorDefinition(connector)) {
      return (connector as Connector).connector;
    }
    else if (isConnectorInstance(connector)) {
      return connector;
    }
    else {
      console.error('Unknown connector type: ', connector);
      return null;
    }
  }

  /**
   * Extracts parameters from a connector element
   */
  private extractParameters(
    datapoint: ConnectorParameters['elements'][0],
    input?: object
  ): { ref: string | null, parameters: JSONObject | null } {
    let ref: string = null;
    let parameters: JSONObject = null;

    if (isConnectorTask(datapoint) || isDatapointInstance(datapoint)) {
      if ('ref' in datapoint) {
        ref = datapoint.ref;
      }
      if (input && ref && input[ref]) {
        parameters = { ...datapoint.parameters, ...input[ref] };
      }
      else if (datapoint.parameters) {
        parameters = datapoint.parameters;
      }
      else if (input) {
        parameters = { ...input };
      }
    }

    return { ref, parameters };
  }

  /**
   * Checks if the datapoint should skip processing and just pass input
   */
  private shouldSkipProcessing(
    resolvedConnectivity: ConnectableElement | null,
    index: number
  ): boolean {
    return !resolvedConnectivity || (
      isDatapointDefinition(resolvedConnectivity) &&
      index === 0 &&
      (
        resolvedConnectivity.datapoint.type === DefaultDatapointTypes.EVENT ||
        resolvedConnectivity.datapoint.type === DefaultDatapointTypes.WINDOW_MESSAGE
      )
    );
  }


  async dispatchConnector(
    connector: Connector | ConnectorInstance,
    input?: object
  ): Promise<unknown> {
    const connectorConfig = this.getConnectorConfig(connector);
    if (!connectorConfig) {
      return null;
    }

    console.log('connectorConfig: ', connectorConfig);
    // Single object for storing results by ref and for JSONata evaluation
    const resultMap: JSONObject = {};
    let lastResult = null;

    for (let i = 0; i < connectorConfig.elements.length; i++) {
      const element = connectorConfig.elements[i];
      const { ref, parameters } = this.extractParameters(element, input);
      const resolvedConnectivity = getDatapointFromConnectorElement(element, this.connectivityHolder);

      // If no datapoint that should be dispatched (e.g. event as first element in connector) just take the input as result
      if (this.shouldSkipProcessing(resolvedConnectivity, i)) {
        if (ref) {
          resultMap[ref] = input as JSONValue;
        }
        continue;
      }

      const placeholderParams = {
        input: lastResult as JSONObject,
        root: typeof (input) == 'object' ? input as JSONObject : {},
        ref: resultMap,
        params: parameters,
        env: ENV_VARS.getAll()
      };

      const fullPayload = { ...parameters };

      const resolvedPayload = await replacePlaceholdersObjectLocal(fullPayload, placeholderParams);
      
      // Protect only unresolvable workflow references before placeholder resolution
      const { dataWithProtectedRefs, refMap } = protectWorkflowReferences(resolvedConnectivity, placeholderParams);
      const resolvedDatapointTaskWithProtection = await replacePlaceholdersObjectLocal(dataWithProtectedRefs as Datapoint, placeholderParams);
      const resolvedDatapointTask = restoreWorkflowReferences(resolvedDatapointTaskWithProtection, refMap);

      console.log('invoking endpoint with----------', resolvedPayload);

      const result = await this.connectivityManager.invoke({
        id: resolvedConnectivity.id,
        task: resolvedDatapointTask as unknown as ConnectableElement,
        input: resolvedPayload
      });

      lastResult = result;

      if (ref) {
        resultMap[ref] = result as JSONValue;
      }
    }

    return lastResult;
  }

  async dispatchDatapoint(
    datapoint: ConnectivityDatapoint,
    input?: object,
    props?: DispatchDatapointProps
  ): Promise<unknown> {

    const placeholderParams = {
      input: input as JSONObject,
      params: (datapoint.datapoint.parameters ?? {}) as JSONObject | JSAPIInstanceParameters,
      env: ENV_VARS.getAll()
    };

    datapoint = await replacePlaceholdersLocal(datapoint, placeholderParams) as ConnectivityDatapoint;
    input = await replacePlaceholdersObjectLocal(input, placeholderParams);

    if (datapoint.datapoint.type === DefaultDatapointTypes.EVENT) {
      const eventDatapoint = datapoint.datapoint as EventDatapointParameters;
      return this.dispatchEvent(eventDatapoint.event.topic, input);
    }
    if (datapoint.datapoint.type === DefaultDatapointTypes.JS_API) {
      const parameters = props?.parameters;
      let componentInstanceID = '';
      if (parameters) {
        componentInstanceID = parameters.componentInstanceID;
      } else {
        componentInstanceID = datapoint.parameters?.componentInstanceID as string ||
          (input && 'componentInstanceID' in input && input?.componentInstanceID) as string;
      }
      const { ...datapointRest } = datapoint.parameters || {};
      const { ...propsRest } = parameters || {};
      const jsAPIDatapoint = datapoint.datapoint as JSAPIDatapointParameters;
      if (!componentInstanceID || componentInstanceID.length === 0) {
        componentInstanceID = jsAPIDatapoint.jsAPI.parameters?.componentInstanceID as string;
      }
      return this.dispatchJSAPI({
        apiName: jsAPIDatapoint.jsAPI.api,
        componentInstanceID,
        input,
        props: { ...datapointRest, ...propsRest },
      });
    }
    if (this.windowDispatcher.canDispatch({ id: datapoint.id, datapoint })) {
      return await this.windowDispatcher.dispatch({
        id: datapoint.id,
        task: datapoint as unknown as ConnectableElement,
        input,
      });
    }
    console.warn(
      'Currently unsupported datapointtype for client side: ' + datapoint.datapoint.type
    );
    return null;
  }


  dispatchEvent(topic: string, params: object) {
    this.eventManager.emit(topic, params);
  }

  dispatchJSAPI({
    apiName,
    componentInstanceID,
    input,
    props,
  }: {
    apiName: string;
    componentInstanceID: string;
    input: object;
    props: object;
  }) {
    const apiID = componentInstanceID + '-' + apiName;
    const api = this.apiManager.get(apiID);
    if (api) {
      return api({
        ...props,
        ...input
      });
    } else {
      console.error('Could not find api with id ' + apiID);
    }
  }
}

function protectWorkflowReferences(
  data: unknown,
  placeholderParams?: { ref?: JSONObject }
): { dataWithProtectedRefs: unknown; refMap: Map<string, string> } {
  const refMap = new Map<string, string>();
  let refCounter = 0;
  const refContext = placeholderParams?.ref || {};
  
  const processValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
      // Match ${ref.*} patterns - workflow references
      const refPatternWithBraces = /\$\{(ref\.[^}]+)\}/g;
      let result = value;
      let match;
      
      // Collect all matches first
      const matches: Array<{ fullMatch: string; refExpr: string }> = [];
      while ((match = refPatternWithBraces.exec(value)) !== null) {
        matches.push({
          fullMatch: match[0],
          refExpr: match[1]
        });
      }
      
      // Process each match
      matches.forEach(({ fullMatch, refExpr }) => {
        // Extract all ref.X.Y.Z patterns from the expression
        const refPaths = extractRefPaths(refExpr);
        
        // Check if ANY of the references cannot be resolved
        const shouldProtect = refPaths.some(path => !canResolveRef(path, refContext));
        
        if (shouldProtect) {
          const placeholder = `__WORKFLOW_REF_${refCounter}__`;
          refMap.set(placeholder, fullMatch);
          result = result.replace(fullMatch, placeholder);
          refCounter++;
        }
      });
      
      return result;
    } else if (Array.isArray(value)) {
      return value.map(processValue);
    } else if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        result[key] = processValue(val);
      }
      return result;
    }
    return value;
  };
  
  return {
    dataWithProtectedRefs: processValue(data),
    refMap
  };
}

function extractRefPaths(expression: string): string[] {
  // Extract all ref.X.Y.Z patterns from a JSONata expression
  // Match ref followed by dot notation (alphanumeric and underscores)
  const pattern = /ref\.\w+(?:\.\w+)*/g;
  const matches = expression.match(pattern) || [];
  return matches;
}

function canResolveRef(refPath: string, refContext: JSONObject): boolean {
  // Check if a ref path like "ref.bindButton.tag" can be resolved
  // Remove "ref." prefix and traverse the path
  const path = refPath.replace(/^ref\./, '').split('.');
  
  let current: JSONObject | unknown = refContext;
  for (const part of path) {
    if (current && typeof current === 'object' && part in (current as JSONObject)) {
      current = (current as JSONObject)[part];
    } else {
      return false;
    }
  }
  return true;
}

function restoreWorkflowReferences(data: unknown, refMap: Map<string, string>): unknown {
  const processValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
      let result = value;
      refMap.forEach((originalRef, placeholder) => {
        result = result.replace(placeholder, originalRef);
      });
      return result;
    } else if (Array.isArray(value)) {
      return value.map(processValue);
    } else if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        result[key] = processValue(val);
      }
      return result;
    }
    return value;
  };
  
  return processValue(data);
}
